import { AlertDialog as AlertDialogPrimitive } from "radix-ui";
import type * as React from "react";
import { Button } from "./button";

/**
 * Primer's confirmation dialog for destructive actions, in place of `window.confirm`.
 * `children` is the trigger; focus starts on Cancel so Enter never confirms by accident.
 * Pass `open`/`onOpenChange` instead of a trigger to open it from elsewhere, such as a menu item.
 */
function ConfirmDialog({
  title,
  description,
  confirmLabel,
  onConfirm,
  open,
  onOpenChange,
  children,
}: {
  title: React.ReactNode;
  description: React.ReactNode;
  confirmLabel: React.ReactNode;
  onConfirm: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children?: React.ReactElement;
}) {
  return (
    <AlertDialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      {children && <AlertDialogPrimitive.Trigger asChild>{children}</AlertDialogPrimitive.Trigger>}
      <AlertDialogPrimitive.Portal>
        <AlertDialogPrimitive.Overlay className="fixed inset-0 z-50 bg-overlay data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" />
        <AlertDialogPrimitive.Content
          data-slot="confirm-dialog"
          className="fixed left-1/2 top-1/2 z-50 w-[min(440px,calc(100vw-32px))] -translate-x-1/2 -translate-y-1/2 rounded-xl border bg-popover text-popover-foreground shadow-floating outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95"
        >
          <div className="border-b border-border-muted px-4 py-3">
            <AlertDialogPrimitive.Title className="text-sm font-semibold">{title}</AlertDialogPrimitive.Title>
          </div>
          <AlertDialogPrimitive.Description className="px-4 py-4 text-sm text-muted-foreground">{description}</AlertDialogPrimitive.Description>
          <div className="flex justify-end gap-2 border-t border-border-muted px-4 py-3">
            <AlertDialogPrimitive.Cancel asChild>
              <Button type="button">Cancel</Button>
            </AlertDialogPrimitive.Cancel>
            <AlertDialogPrimitive.Action asChild>
              <Button type="button" variant="danger" onClick={onConfirm}>
                {confirmLabel}
              </Button>
            </AlertDialogPrimitive.Action>
          </div>
        </AlertDialogPrimitive.Content>
      </AlertDialogPrimitive.Portal>
    </AlertDialogPrimitive.Root>
  );
}

export { ConfirmDialog };
